// src/services/chequeService.js

import axiosInstance from './axiosConfig';

// ✅ role normalizer (supports SUPERADMIN, superadmin, Admin, etc.)
const normalizeRole = (role) => role?.toLowerCase();

const chequeService = {
  /**
   * Get all cheques from cash movements (filtered by type = "CHEQUE")
   */
  getAll: async (page = 1, limit = 10, userRole = null, userId = null) => {
    const role = normalizeRole(userRole);

    console.log('chequeService.getAll called with:', {
      page,
      limit,
      role,
      userId,
    });

    try {
      // ✅ Fetch all cash movements (INCREASE LIMIT)
      const response = await axiosInstance.get('/cash/movements', {
        params: {
          page: 1,
          limit: 10000,
        },
      });
      
      const movements = response.data?.data?.movements || [];
      
      // ✅ Filter only CHEQUE type movements
      let cheques = movements.filter(movement => movement.type === 'CHEQUE');

      // ✅ Apply role-based filtering
      if (role === 'admin' && userId) {
        // Admin sees only their cheques (filter by salesPersonId)
        cheques = cheques.filter(cheque => cheque.salesPersonId === userId);
      } else if (role === 'superadmin') {
        // SuperAdmin sees all cheques (no additional filtering needed)
      } else if (role !== 'superadmin') {
        throw new Error(`Invalid user role: ${userRole}`);
      }

      // ✅ Apply pagination manually
      const startIndex = (page - 1) * limit;
      const endIndex = startIndex + limit;
      const paginatedCheques = cheques.slice(startIndex, endIndex);

      return {
        data: paginatedCheques,
        total: cheques.length,
        page,
        limit,
        totalPages: Math.ceil(cheques.length / limit),
      };
    } catch (error) {
      console.error('Error fetching cheques:', error);
      throw error;
    }
  },

  /**
   * Get cheque by ID from cash movements
   */
  getById: async (id) => {
    const response = await axiosInstance.get('/cash/movements', {
      params: {
        page: 1,
        limit: 10000,
      },
    });

    const movements = response.data?.data?.movements || [];
    
    const cheque = movements.find(
      movement => movement.type === 'CHEQUE' && movement.id === id
    );
    
    if (!cheque) {
      throw new Error(`Cheque with ID ${id} not found`);
    }
    
    return cheque;
  },

  /**
   * Get active cheques (for dropdowns)
   */
  getActive: async (userRole = null, userId = null) => {
    const role = normalizeRole(userRole);
    
    const response = await axiosInstance.get('/cash/movements', {
      params: {
        page: 1,
        limit: 10000,
      },
    });

    const movements = response.data?.data?.movements || [];
    
    let cheques = movements.filter(movement => movement.type === 'CHEQUE');

    // ✅ Apply role-based filtering
    if (role === 'admin' && userId) {
      cheques = cheques.filter(cheque => cheque.salesPersonId === userId);
    } else if (role !== 'superadmin') {
      throw new Error(`Invalid user role: ${userRole}`);
    }

    return cheques.map((chq) => ({
      value: chq.id,
      text: chq.reference || chq.description || `Cheque ${chq.id}`,
    }));
  },

  /**
   * Create a new cheque (if your API supports this)
   * Note: You may need to adjust this endpoint based on your backend
   */
  create: async (data) => {
    const response = await axiosInstance.post('/cheques/create', data);
    return response.data;
  },

  /**
   * Update a cheque (if your API supports this)
   */
  update: async (id, data) => {
    const response = await axiosInstance.put(`/cheques/edit/${id}`, data);
    return response.data;
  },

  /**
   * Delete a cheque (if your API supports this)
   */
  delete: async (id) => {
    const response = await axiosInstance.delete(`/cheques/delete/${id}`);
    return response.data;
  },

  /**
   * Filter cheques based on criteria
   */
  filter: async (filters = {}, userRole = null, userId = null) => {
    const role = normalizeRole(userRole);
    
    const response = await axiosInstance.get('/cash/movements', {
      params: {
        page: 1,
        limit: 10000,
      },
    });

    const movements = response.data?.data?.movements || [];
    
    let cheques = movements.filter(movement => movement.type === 'CHEQUE');

    // ✅ Apply role-based filtering
    if (role === 'admin' && userId) {
      cheques = cheques.filter(cheque => cheque.salesPersonId === userId);
    } else if (role !== 'superadmin') {
      throw new Error(`Invalid user role: ${userRole}`);
    }

    // ✅ Apply filters
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      cheques = cheques.filter(
        cheque =>
          cheque.description?.toLowerCase().includes(searchLower) ||
          cheque.reference?.toLowerCase().includes(searchLower) ||
          cheque.salesPerson?.toLowerCase().includes(searchLower)
      );
    }

    if (filters.startDate) {
      cheques = cheques.filter(
        cheque => new Date(cheque.date) >= new Date(filters.startDate)
      );
    }

    if (filters.endDate) {
      cheques = cheques.filter(
        cheque => new Date(cheque.date) <= new Date(filters.endDate)
      );
    }

    if (filters.inventory) {
      cheques = cheques.filter(
        cheque => cheque.inventory === filters.inventory
      );
    }

    return cheques;
  },
};

export default chequeService;
